import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  Task,
  TaskStatus,
  TaskPriority,
  TaskAnalytics,
  TaskSubtask,
} from '@/models/team-task.model';
import {
  auditCreate,
  auditUpdate,
  auditDelete,
} from '@/utils/audit-integration';
import { AuditEntityType, AuditLogAction } from '@/models/audit-log.model';
import { AuditLogService } from './audit-log.service';

const TASKS_COLLECTION = 'teamTasks';

export class TeamTaskService {
  /**
   * Remove undefined values from an object (Firestore doesn't accept undefined)
   */
  private static removeUndefined<T extends Record<string, any>>(obj: T): T {
    const result = {} as T;
    for (const key of Object.keys(obj)) {
      if (obj[key] !== undefined) {
        result[key as keyof T] = obj[key];
      }
    }
    return result;
  }

  /**
   * Create a new task (typically from AI extraction)
   */
  static async createTask(
    taskData: Partial<Task>,
    userId: string = 'ai-system',
    userName: string = 'AI System'
  ): Promise<string> {
    const now = Timestamp.now();

    const task: Partial<Task> = this.removeUndefined({
      ...taskData,
      status: taskData.status || TaskStatus.draft,
      priority: taskData.priority || TaskPriority.medium,
      containsPHI: taskData.containsPHI || false,
      phiDataTypes: taskData.phiDataTypes || [],
      tags: taskData.tags || [],
      dependsOnTaskIds: taskData.dependsOnTaskIds || [],
      blockedByTaskIds: taskData.blockedByTaskIds || [],
      relatedDecisionIds: taskData.relatedDecisionIds || [],
      relatedRiskIds: taskData.relatedRiskIds || [],
      assignmentApprovalRequired: taskData.assignmentApprovalRequired !== false,
      dueDateApprovalRequired: taskData.dueDateApprovalRequired !== false,
      subtasks: taskData.subtasks || [],
      createdBy: userId,
      createdByName: userName,
      createdAt: now,
      updatedAt: now,
    });

    const docRef = await addDoc(collection(db, TASKS_COLLECTION), task);

    // Audit log - don't fail the operation if audit fails
    try {
      await auditCreate(
        AuditEntityType.teamTask,
        docRef.id,
        taskData.title || 'Untitled Task'
      );
    } catch (auditError) {
      console.warn('Audit log failed for task creation:', auditError);
    }

    return docRef.id;
  }

  /**
   * Get task by ID
   */
  static async getTaskById(id: string): Promise<Task | null> {
    const docRef = doc(db, TASKS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return this.docToTask(docSnap.id, docSnap.data());
  }

  /**
   * Get all tasks for a meeting
   */
  static async getTasksByMeeting(meetingId: string): Promise<Task[]> {
    const q = query(
      collection(db, TASKS_COLLECTION),
      where('meetingId', '==', meetingId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToTask(doc.id, doc.data()));
  }

  /**
   * Get all tasks
   */
  static async getAllTasks(): Promise<Task[]> {
    const q = query(
      collection(db, TASKS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToTask(doc.id, doc.data()));
  }

  /**
   * Get tasks by status
   */
  static async getTasksByStatus(status: TaskStatus): Promise<Task[]> {
    const q = query(
      collection(db, TASKS_COLLECTION),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToTask(doc.id, doc.data()));
  }

  /**
   * Get tasks by priority
   */
  static async getTasksByPriority(priority: TaskPriority): Promise<Task[]> {
    const q = query(
      collection(db, TASKS_COLLECTION),
      where('priority', '==', priority),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToTask(doc.id, doc.data()));
  }

  /**
   * Get tasks assigned to a user
   */
  static async getTasksByAssignee(assigneeId: string): Promise<Task[]> {
    const q = query(
      collection(db, TASKS_COLLECTION),
      where('assigneeId', '==', assigneeId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToTask(doc.id, doc.data()));
  }

  /**
   * Get overdue tasks
   */
  static async getOverdueTasks(): Promise<Task[]> {
    const now = Timestamp.now();
    const q = query(
      collection(db, TASKS_COLLECTION),
      where('dueDate', '<', now),
      where('status', 'not-in', [TaskStatus.completed, TaskStatus.cancelled])
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToTask(doc.id, doc.data()));
  }

  /**
   * Search tasks by title or description
   */
  static async searchTasks(searchTerm: string): Promise<Task[]> {
    const allTasks = await this.getAllTasks();
    const lowerSearch = searchTerm.toLowerCase();

    return allTasks.filter(task =>
      task.title.toLowerCase().includes(lowerSearch) ||
      task.description.toLowerCase().includes(lowerSearch) ||
      task.category?.toLowerCase().includes(lowerSearch)
    );
  }

  /**
   * Update task
   */
  static async updateTask(
    id: string,
    updates: Partial<Task>,
    userId?: string
  ): Promise<void> {
    const docRef = doc(db, TASKS_COLLECTION, id);

    const updateData: any = this.removeUndefined({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });

    await updateDoc(docRef, updateData);

    // Audit log - don't fail the operation if audit fails
    try {
      await auditUpdate(
        AuditEntityType.teamTask,
        id,
        updates.title || 'Task'
      );
    } catch (auditError) {
      console.warn('Audit log failed for task update:', auditError);
    }
  }

  /**
   * Update task status
   */
  static async updateTaskStatus(
    id: string,
    status: TaskStatus,
    userId?: string
  ): Promise<void> {
    await this.updateTask(id, { status }, userId);
  }

  /**
   * Delete task
   */
  static async deleteTask(id: string): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    const docRef = doc(db, TASKS_COLLECTION, id);
    await deleteDoc(docRef);

    // Audit log - don't fail the operation if audit fails
    try {
      await auditDelete(
        AuditEntityType.teamTask,
        id,
        task.title
      );
    } catch (auditError) {
      console.warn('Audit log failed for task delete:', auditError);
    }
  }

  /**
   * Assign task to a user
   */
  static async assignTask(
    id: string,
    assigneeId: string,
    assigneeName: string,
    assigneeEmail: string,
    assignedBy?: string
  ): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    await this.updateTask(id, {
      assigneeId,
      assigneeName,
      assigneeEmail,
      status: TaskStatus.assigned,
    }, assignedBy);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.taskAssigned,
      severity: 'info',
      description: `Task assigned to ${assigneeName}: ${task.title}`,
      userId: assignedBy,
      entityType: AuditEntityType.teamTask,
      entityId: id,
      entityName: task.title,
      success: true,
      details: {
        assigneeId,
        assigneeName,
        assigneeEmail,
      },
    });
  }

  /**
   * Approve task assignment
   */
  static async approveAssignment(
    id: string,
    approverId: string,
    approverName: string,
    comments?: string
  ): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    await this.updateTask(id, {
      assignmentApprovalRequired: false,
      assignmentApprovedBy: approverId,
      assignmentApprovedAt: new Date(),
      status: TaskStatus.assigned,
    }, approverId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.approvalStepCompleted,
      severity: 'info',
      description: `Task assignment approved: ${task.title}`,
      userId: approverId,
      entityType: AuditEntityType.teamTask,
      entityId: id,
      entityName: task.title,
      success: true,
      details: {
        approverName,
        assignee: task.assigneeName,
        comments,
      },
    });
  }

  /**
   * Set task due date
   */
  static async setDueDate(
    id: string,
    dueDate: Date,
    userId?: string
  ): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    await this.updateTask(id, {
      dueDate,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.taskUpdated,
      severity: 'info',
      description: `Task due date set: ${task.title}`,
      userId,
      entityType: AuditEntityType.teamTask,
      entityId: id,
      entityName: task.title,
      success: true,
      details: {
        dueDate: dueDate.toISOString(),
      },
    });
  }

  /**
   * Approve task due date
   */
  static async approveDueDate(
    id: string,
    approverId: string,
    approverName: string,
    comments?: string
  ): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    await this.updateTask(id, {
      dueDateApprovalRequired: false,
      dueDateApprovedBy: approverId,
      dueDateApprovedAt: new Date(),
    }, approverId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.approvalStepCompleted,
      severity: 'info',
      description: `Task due date approved: ${task.title}`,
      userId: approverId,
      entityType: AuditEntityType.teamTask,
      entityId: id,
      entityName: task.title,
      success: true,
      details: {
        approverName,
        dueDate: task.dueDate?.toISOString(),
        comments,
      },
    });
  }

  /**
   * Approve a task (general approval workflow)
   */
  static async approveTask(
    id: string,
    approverId: string,
    approverName: string,
    comments?: string
  ): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    // Update approval workflow
    const workflow = task.approvalWorkflow || [];
    const currentStep = workflow.find(step => step.status === 'pending');

    if (currentStep) {
      currentStep.status = 'approved';
      currentStep.approverId = approverId;
      currentStep.approverName = approverName;
      currentStep.actionDate = new Date();
      currentStep.comments = comments;
    }

    // Check if all steps are approved
    const allApproved = workflow.every(step => step.status === 'approved');

    await this.updateTask(id, {
      approvalWorkflow: workflow,
      status: allApproved ? TaskStatus.approved : TaskStatus.pendingApproval,
      approvedBy: allApproved ? approverId : undefined,
      approvedByName: allApproved ? approverName : undefined,
      approvedAt: allApproved ? new Date() : undefined,
    }, approverId);

    // Audit log
    await AuditLogService.writeLog({
      action: allApproved ? AuditLogAction.taskApproved : AuditLogAction.approvalStepCompleted,
      severity: 'info',
      description: `Task ${allApproved ? 'approved' : 'approval step completed'}: ${task.title}`,
      userId: approverId,
      entityType: AuditEntityType.teamTask,
      entityId: id,
      entityName: task.title,
      success: true,
      details: {
        approverName,
        allApproved,
        comments,
      },
    });
  }

  /**
   * Start working on a task
   */
  static async startTask(id: string, userId?: string): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    if (task.status !== TaskStatus.assigned && task.status !== TaskStatus.approved) {
      throw new Error('Task must be assigned or approved before starting');
    }

    await this.updateTask(id, {
      status: TaskStatus.inProgress,
      startedAt: new Date(),
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.taskStarted,
      severity: 'info',
      description: `Task started: ${task.title}`,
      userId,
      entityType: AuditEntityType.teamTask,
      entityId: id,
      entityName: task.title,
      success: true,
    });
  }

  /**
   * Block a task
   */
  static async blockTask(
    id: string,
    reason: string,
    userId?: string
  ): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    await this.updateTask(id, {
      status: TaskStatus.blocked,
      blockedReason: reason,
      blockedAt: new Date(),
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.taskBlocked,
      severity: 'warning',
      description: `Task blocked: ${task.title}`,
      userId,
      entityType: AuditEntityType.teamTask,
      entityId: id,
      entityName: task.title,
      success: true,
      details: {
        reason,
      },
    });
  }

  /**
   * Unblock a task
   */
  static async unblockTask(id: string, userId?: string): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    if (task.status !== TaskStatus.blocked) {
      throw new Error('Task is not blocked');
    }

    await this.updateTask(id, {
      status: TaskStatus.inProgress,
      blockedReason: undefined,
      blockedAt: undefined,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.taskUpdated,
      severity: 'info',
      description: `Task unblocked: ${task.title}`,
      userId,
      entityType: AuditEntityType.teamTask,
      entityId: id,
      entityName: task.title,
      success: true,
    });
  }

  /**
   * Complete a task
   */
  static async completeTask(id: string, userId?: string): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    await this.updateTask(id, {
      status: TaskStatus.completed,
      completedAt: new Date(),
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.taskCompleted,
      severity: 'info',
      description: `Task completed: ${task.title}`,
      userId,
      entityType: AuditEntityType.teamTask,
      entityId: id,
      entityName: task.title,
      success: true,
    });
  }

  /**
   * Cancel a task
   */
  static async cancelTask(
    id: string,
    reason: string,
    userId?: string
  ): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    await this.updateTask(id, {
      status: TaskStatus.cancelled,
      cancelledReason: reason,
      cancelledAt: new Date(),
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.taskUpdated,
      severity: 'info',
      description: `Task cancelled: ${task.title}`,
      userId,
      entityType: AuditEntityType.teamTask,
      entityId: id,
      entityName: task.title,
      success: true,
      details: {
        reason,
      },
    });
  }

  /**
   * Add a subtask
   */
  static async addSubtask(
    id: string,
    subtaskTitle: string,
    userId?: string
  ): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    const subtask: TaskSubtask = {
      id: `subtask-${Date.now()}`,
      title: subtaskTitle,
      isCompleted: false,
    };

    const subtasks = [...(task.subtasks || []), subtask];

    await this.updateTask(id, { subtasks }, userId);
  }

  /**
   * Complete a subtask
   */
  static async completeSubtask(
    id: string,
    subtaskId: string,
    userId?: string,
    userName?: string
  ): Promise<void> {
    const task = await this.getTaskById(id);
    if (!task) {
      throw new Error('Task not found');
    }

    const subtasks = task.subtasks?.map(st =>
      st.id === subtaskId
        ? {
            ...st,
            isCompleted: true,
            completedAt: new Date(),
            completedBy: userName,
          }
        : st
    );

    await this.updateTask(id, { subtasks }, userId);
  }

  /**
   * Get task analytics
   */
  static async getTaskAnalytics(
    dateRange?: { start: Date; end: Date }
  ): Promise<TaskAnalytics> {
    let tasks: Task[];

    if (dateRange) {
      const q = query(
        collection(db, TASKS_COLLECTION),
        where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
      );
      const snapshot = await getDocs(q);
      tasks = snapshot.docs.map(doc => this.docToTask(doc.id, doc.data()));
    } else {
      tasks = await this.getAllTasks();
    }

    const byStatus: Record<TaskStatus, number> = {
      [TaskStatus.draft]: 0,
      [TaskStatus.pendingApproval]: 0,
      [TaskStatus.approved]: 0,
      [TaskStatus.assigned]: 0,
      [TaskStatus.inProgress]: 0,
      [TaskStatus.blocked]: 0,
      [TaskStatus.completed]: 0,
      [TaskStatus.cancelled]: 0,
    };

    const byPriority: Record<TaskPriority, number> = {
      [TaskPriority.low]: 0,
      [TaskPriority.medium]: 0,
      [TaskPriority.high]: 0,
      [TaskPriority.urgent]: 0,
    };

    let totalCompletionTime = 0;
    let completedCount = 0;
    let overdueCount = 0;
    let blockedCount = 0;
    const now = new Date();

    tasks.forEach(task => {
      byStatus[task.status]++;
      byPriority[task.priority]++;

      if (task.status === TaskStatus.completed && task.startedAt && task.completedAt) {
        const timeDiff = task.completedAt.getTime() - task.startedAt.getTime();
        totalCompletionTime += timeDiff / (1000 * 60 * 60 * 24); // Convert to days
        completedCount++;
      }

      if (task.dueDate && task.dueDate < now && task.status !== TaskStatus.completed && task.status !== TaskStatus.cancelled) {
        overdueCount++;
      }

      if (task.status === TaskStatus.blocked) {
        blockedCount++;
      }
    });

    const totalTasks = tasks.length;
    const completionRate = totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0;

    return {
      totalTasks,
      byStatus,
      byPriority,
      completionRate,
      avgCompletionTime: completedCount > 0 ? totalCompletionTime / completedCount : 0,
      overdueCount,
      blockedCount,
    };
  }

  /**
   * Convert Firestore document to Task model
   */
  private static docToTask(id: string, data: any): Task {
    return {
      id,
      meetingId: data.meetingId || '',
      meetingTitle: data.meetingTitle || '',
      sourceTimestampStart: data.sourceTimestampStart,
      sourceTimestampEnd: data.sourceTimestampEnd,
      sourceTextSnippet: data.sourceTextSnippet,
      title: data.title || '',
      description: data.description || '',
      acceptanceCriteria: data.acceptanceCriteria || [],
      assigneeId: data.assigneeId,
      assigneeName: data.assigneeName,
      assigneeEmail: data.assigneeEmail,
      assignmentApprovalRequired: data.assignmentApprovalRequired !== false,
      assignmentApprovedBy: data.assignmentApprovedBy,
      assignmentApprovedAt: data.assignmentApprovedAt?.toDate(),
      dueDate: data.dueDate?.toDate(),
      dueDateApprovalRequired: data.dueDateApprovalRequired !== false,
      dueDateApprovedBy: data.dueDateApprovedBy,
      dueDateApprovedAt: data.dueDateApprovedAt?.toDate(),
      estimatedHours: data.estimatedHours,
      priority: data.priority || TaskPriority.medium,
      category: data.category || '',
      status: data.status || TaskStatus.draft,
      approvalWorkflow: data.approvalWorkflow || [],
      startedAt: data.startedAt?.toDate(),
      completedAt: data.completedAt?.toDate(),
      blockedReason: data.blockedReason,
      blockedAt: data.blockedAt?.toDate(),
      cancelledReason: data.cancelledReason,
      cancelledAt: data.cancelledAt?.toDate(),
      subtasks: data.subtasks || [],
      dependsOnTaskIds: data.dependsOnTaskIds || [],
      blockedByTaskIds: data.blockedByTaskIds || [],
      relatedDecisionIds: data.relatedDecisionIds || [],
      relatedRiskIds: data.relatedRiskIds || [],
      containsPHI: data.containsPHI || false,
      phiDataTypes: data.phiDataTypes || [],
      aiConfidence: data.aiConfidence || 0,
      aiExtractionNotes: data.aiExtractionNotes,
      tags: data.tags || [],
      projectId: data.projectId,
      customFields: data.customFields,
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      approvedBy: data.approvedBy,
      approvedByName: data.approvedByName,
      approvedAt: data.approvedAt?.toDate(),
    };
  }
}
